import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    BookOpen, 
    Plus, 
    Edit2, 
    Trash2, 
    RefreshCw,
    Package,
    ChevronRight,
    GripVertical,
    Save
} from 'lucide-react';

const FormulasAdminPage = () => {
    const { api, hasPermission } = useAuth();
    const [loading, setLoading] = useState(true);
    const [formulas, setFormulas] = useState([]);
    const [products, setProducts] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingFormula, setEditingFormula] = useState(null);
    
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        is_active: true,
        steps: []
    });

    // Allow access for Admin, Directeur, Directeur Adjoint
    const { user } = useAuth();
    const role = user?.role || '';
    const canManage = ['ADMIN', 'admin', 'DIRECTEUR', 'DIRECTEUR_ADJOINT'].includes(role);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [formulasRes, productsRes] = await Promise.all([
                api().get('/pos/formulas'),
                api().get('/pos/products')
            ]);
            setFormulas(formulasRes.data || []);
            setProducts(productsRes.data || []);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingFormula(null);
        setFormData({
            name: '',
            description: '',
            price: '',
            is_active: true,
            steps: [
                { name: 'Entrée', order: 1, choices: [] },
                { name: 'Plat', order: 2, choices: [] },
                { name: 'Dessert', order: 3, choices: [] }
            ]
        });
        setDialogOpen(true);
    };

    const handleEdit = (formula) => {
        setEditingFormula(formula);
        setFormData({
            name: formula.name,
            description: formula.description || '',
            price: formula.price?.toString() || '',
            is_active: formula.is_active !== false,
            steps: formula.steps || []
        });
        setDialogOpen(true);
    };

    const handleDelete = async (formulaId) => {
        if (!window.confirm('Supprimer cette formule ?')) return;
        try {
            await api().delete(`/pos/formulas/${formulaId}`);
            toast.success('Formule supprimée');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (formData.steps.length === 0) {
            toast.error('Ajoutez au moins une étape');
            return;
        }

        try {
            const payload = {
                name: formData.name,
                description: formData.description,
                price: parseFloat(formData.price) || 0,
                is_active: formData.is_active,
                steps: formData.steps.map((step, idx) => ({
                    name: step.name,
                    order: idx + 1,
                    choices: step.choices.map(choice => ({
                        product_id: choice.product_id,
                        price_supplement: choice.price_supplement || 0
                    }))
                }))
            };

            if (editingFormula) {
                await api().put(`/pos/formulas/${editingFormula.id}`, payload);
                toast.success('Formule mise à jour');
            } else {
                await api().post('/pos/formulas', payload);
                toast.success('Formule créée');
            }
            
            setDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const addStep = () => {
        setFormData({
            ...formData,
            steps: [...formData.steps, { name: '', order: formData.steps.length + 1, choices: [] }]
        });
    };

    const removeStep = (index) => {
        setFormData({
            ...formData,
            steps: formData.steps.filter((_, i) => i !== index)
        });
    };

    const updateStep = (index, field, value) => {
        const newSteps = [...formData.steps];
        newSteps[index] = { ...newSteps[index], [field]: value };
        setFormData({ ...formData, steps: newSteps });
    };

    const addChoice = (stepIndex, productId) => {
        const product = products.find(p => p.id === productId);
        if (!product) return;
        
        const newSteps = [...formData.steps];
        const exists = newSteps[stepIndex].choices.find(c => c.product_id === productId);
        if (exists) {
            toast.error('Produit déjà ajouté à cette étape');
            return;
        }
        
        newSteps[stepIndex].choices.push({
            product_id: productId,
            product_name: product.name,
            price_supplement: 0
        });
        setFormData({ ...formData, steps: newSteps });
    };

    const removeChoice = (stepIndex, choiceIndex) => {
        const newSteps = [...formData.steps];
        newSteps[stepIndex].choices.splice(choiceIndex, 1);
        setFormData({ ...formData, steps: newSteps });
    };

    const updateChoiceSupplement = (stepIndex, choiceIndex, value) => {
        const newSteps = [...formData.steps];
        newSteps[stepIndex].choices[choiceIndex].price_supplement = parseFloat(value) || 0;
        setFormData({ ...formData, steps: newSteps });
    };

    const formatPrice = (value) => {
        return new Intl.NumberFormat('fr-FR').format(value) + ' F';
    };

    if (!canManage) {
        return (
            <div className="p-6">
                <Card className="max-w-md mx-auto">
                    <CardContent className="pt-6 text-center">
                        <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <h2 className="text-lg font-semibold mb-2">Accès Restreint</h2>
                        <p className="text-muted-foreground">
                            Vous n'avez pas les permissions pour gérer les formules.
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="p-4 lg:p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <BookOpen className="w-7 h-7 text-primary" />
                        Gestion des Formules
                    </h1>
                    <p className="text-muted-foreground">
                        Créez et gérez les menus à prix fixe
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={fetchData} disabled={loading}>
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Actualiser
                    </Button>
                    <Button onClick={handleCreate}>
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvelle formule
                    </Button>
                </div>
            </div>

            {/* Formulas List */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {loading ? (
                    <div className="col-span-full py-8 text-center">
                        <RefreshCw className="w-8 h-8 mx-auto animate-spin text-muted-foreground" />
                    </div>
                ) : formulas.length === 0 ? (
                    <Card className="col-span-full">
                        <CardContent className="py-8 text-center">
                            <BookOpen className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                            <h3 className="font-medium">Aucune formule</h3>
                            <p className="text-sm text-muted-foreground mb-4">
                                Créez votre première formule (menu à prix fixe)
                            </p>
                            <Button onClick={handleCreate}>
                                <Plus className="w-4 h-4 mr-2" />
                                Créer une formule
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    formulas.map(formula => (
                        <Card key={formula.id} className={!formula.is_active ? 'opacity-60' : ''}>
                            <CardHeader className="pb-2">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <CardTitle className="text-lg">{formula.name}</CardTitle>
                                        {formula.description && (
                                            <p className="text-sm text-muted-foreground mt-1">
                                                {formula.description}
                                            </p>
                                        )}
                                    </div>
                                    <Badge className={formula.is_active ? 'bg-green-500' : 'bg-gray-500'}>
                                        {formula.is_active ? 'Actif' : 'Inactif'}
                                    </Badge>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold text-primary mb-3">
                                    {formatPrice(formula.price)}
                                </div>
                                
                                <div className="space-y-2 mb-4">
                                    {formula.steps?.map((step, idx) => (
                                        <div key={idx} className="flex items-center gap-2 text-sm">
                                            <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                                                {idx + 1}
                                            </span>
                                            <span className="font-medium">{step.name}</span>
                                            <span className="text-muted-foreground">
                                                ({step.choices?.length || 0} choix)
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                
                                <div className="flex gap-2">
                                    <Button 
                                        size="sm" 
                                        variant="outline" 
                                        className="flex-1"
                                        onClick={() => handleEdit(formula)}
                                    >
                                        <Edit2 className="w-4 h-4 mr-1" />
                                        Modifier
                                    </Button>
                                    <Button 
                                        size="sm" 
                                        variant="outline"
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        onClick={() => handleDelete(formula.id)}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <BookOpen className="w-5 h-5 text-primary" />
                            {editingFormula ? 'Modifier la formule' : 'Nouvelle formule'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                        {/* Basic Info */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Nom de la formule *</Label>
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    placeholder="Ex: Menu du jour"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Prix (FCFA) *</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    value={formData.price}
                                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                                    placeholder="Ex: 5000"
                                    required
                                />
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Input
                                value={formData.description}
                                onChange={(e) => setFormData({...formData, description: e.target.value})}
                                placeholder="Description courte (optionnel)"
                            />
                        </div>
                        
                        <div className="flex items-center gap-2">
                            <Switch
                                checked={formData.is_active}
                                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                            />
                            <Label>Formule active</Label>
                        </div>

                        {/* Steps */}
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <Label className="text-base font-semibold">Étapes du menu</Label>
                                <Button type="button" size="sm" variant="outline" onClick={addStep}>
                                    <Plus className="w-4 h-4 mr-1" />
                                    Ajouter étape
                                </Button>
                            </div>
                            
                            {formData.steps.map((step, stepIdx) => (
                                <Card key={stepIdx} className="p-4">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold">
                                            {stepIdx + 1}
                                        </div>
                                        <Input
                                            value={step.name}
                                            onChange={(e) => updateStep(stepIdx, 'name', e.target.value)}
                                            placeholder="Nom de l'étape (ex: Entrée)"
                                            className="flex-1"
                                        />
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="ghost"
                                            className="text-red-500"
                                            onClick={() => removeStep(stepIdx)}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    
                                    {/* Add product to step */}
                                    <div className="mb-3">
                                        <Select onValueChange={(val) => addChoice(stepIdx, val)}>
                                            <SelectTrigger className="w-full">
                                                <SelectValue placeholder="Ajouter un produit..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {products.filter(p => p.is_available).map(p => (
                                                    <SelectItem key={p.id} value={p.id}>
                                                        {p.name} - {formatPrice(p.price)}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    
                                    {/* Choices */}
                                    <div className="space-y-2">
                                        {step.choices.map((choice, choiceIdx) => (
                                            <div 
                                                key={choiceIdx} 
                                                className="flex items-center gap-2 p-2 bg-muted/50 rounded"
                                            >
                                                <Package className="w-4 h-4 text-muted-foreground" />
                                                <span className="flex-1 text-sm">{choice.product_name}</span>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    className="w-24 h-8 text-sm"
                                                    placeholder="Suppl."
                                                    value={choice.price_supplement || ''}
                                                    onChange={(e) => updateChoiceSupplement(stepIdx, choiceIdx, e.target.value)}
                                                />
                                                <span className="text-xs text-muted-foreground">F suppl.</span>
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="ghost"
                                                    className="h-8 w-8 p-0 text-red-500"
                                                    onClick={() => removeChoice(stepIdx, choiceIdx)}
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                </Button>
                                            </div>
                                        ))}
                                        {step.choices.length === 0 && (
                                            <p className="text-sm text-muted-foreground text-center py-2">
                                                Aucun produit dans cette étape
                                            </p>
                                        )}
                                    </div>
                                </Card>
                            ))}
                        </div>
                        
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button type="submit">
                                <Save className="w-4 h-4 mr-2" />
                                {editingFormula ? 'Enregistrer' : 'Créer la formule'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default FormulasAdminPage;
